import * as THREE from 'three'
import { worldWidth, worldDepth } from '../../utils/grid'

// Shared, preloaded PBR surfaces. Level rebuilds own geometry, never these textures.
const materials = {
  limestone: new THREE.MeshStandardMaterial({ color: 0xe2ddd0, roughness: 0.86, bumpScale: 0.075 }),
  sand: new THREE.MeshStandardMaterial({ color: 0xe6dac0, roughness: 0.98, bumpScale: 0.028 }),
  kelp: new THREE.MeshStandardMaterial({ color: 0x548641, roughness: 0.76, side: THREE.DoubleSide }),
  coral: new THREE.MeshStandardMaterial({ color: 0xd88870, roughness: 0.85 }),
}
/** Retain each colony's chapter colour while using the generated sponge pores as
 * neutral surface detail. The source atlas is blue because it also serves BlueBob. */
export function applyReefColony(material: THREE.MeshStandardMaterial): void {
  material.onBeforeCompile = shader => {
    shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', `
      #ifdef USE_MAP
        vec4 pore = texture2D(map, vMapUv);
        diffuseColor.rgb *= vec3(clamp(0.48 + pore.g * 1.8, 0.48, 1.12));
        diffuseColor.a *= pore.a;
      #endif
    `)
  }
  material.customProgramCacheKey = () => 'reef-colony-porous-v1'
}

/** Per-vertex root weights survive static batching, so ribbons move with the same
 * current without translating their roots or allocating animation objects. */
export function applyReefKelp(material: THREE.MeshStandardMaterial): void {
  material.onBeforeCompile = shader => {
    shader.uniforms.reefTime = time
    shader.vertexShader = 'uniform float reefTime; attribute float reefBend;\n' + shader.vertexShader
    shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', `#include <begin_vertex>
      float bend = reefBend * reefBend;
      transformed.x += sin(reefTime * 1.15 + position.x * 0.65 + position.z * 0.4) * bend * 0.075;
      transformed.z += cos(reefTime * 0.8 + position.z * 0.7) * bend * 0.045;
    `)
  }
  material.customProgramCacheKey = () => 'reef-kelp-current-v1'
}
let pending: Promise<void> | undefined
let caustics: THREE.Texture | null = null
const time = { value: 0 }
export function updateReefLight(dt: number): void { time.value += dt }
/** World-projected caustics respect direct-light visibility, including wall shadows. */
export function applyReefLight(material: THREE.MeshStandardMaterial, seabed = false): void {
  material.onBeforeCompile = shader => {
    shader.uniforms.reefTime = time
    shader.uniforms.reefCaustics = { value: caustics }
    shader.uniforms.reefBounds = { value: new THREE.Vector2(worldWidth() * 0.5, worldDepth() * 0.5) }
    shader.vertexShader = 'varying vec3 vReefWorld; varying vec3 vReefSurfaceNormal;\n' + shader.vertexShader
    shader.vertexShader = shader.vertexShader.replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvReefWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvReefSurfaceNormal = inverseTransformDirection(transformedNormal, viewMatrix);')
    shader.fragmentShader = `varying vec3 vReefWorld;
      varying vec3 vReefSurfaceNormal;
      uniform sampler2D reefCaustics;
      uniform float reefTime;
      uniform vec2 reefBounds;
      float reefHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float reefNoise(vec2 p) {
        vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
        return mix(mix(reefHash(i), reefHash(i + vec2(1.,0.)), f.x),
          mix(reefHash(i + vec2(0.,1.)), reefHash(i + vec2(1.,1.)), f.x), f.y);
      }
    ` + shader.fragmentShader
    // A disk filter avoids the repeating grid produced by simply widening Three's
    // nine-tap PCF kernel. This is local to reef surfaces; actor lighting is unchanged.
    const shadowChunk = THREE.ShaderChunk.shadowmap_pars_fragment
    const shadowStart = shadowChunk.indexOf('float getShadow(')
    const shadowEnd = shadowChunk.indexOf('vec2 cubeToUV(')
    const softShadow = `float getShadow(sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity,
      float shadowBias, float shadowRadius, vec4 shadowCoord) {
      vec3 p = shadowCoord.xyz / shadowCoord.w;
      p.z += shadowBias;
      if(p.x < 0.0 || p.x > 1.0 || p.y < 0.0 || p.y > 1.0 || p.z > 1.0) return 1.0;
      float visibility = 0.0;
      for(int i=0; i<8; i++) {
        float angle = float(i) * 2.399963;
        float radius = sqrt((float(i) + 0.5) / 8.0) * 2.8;
        vec2 offset = vec2(cos(angle),sin(angle)) * radius / shadowMapSize;
        visibility += texture2DCompare(shadowMap, p.xy + offset, p.z);
      }
      return mix(1.0, visibility / 8.0, shadowIntensity);
    }
`;
    shader.fragmentShader = shader.fragmentShader.replace('#include <shadowmap_pars_fragment>',
      shadowChunk.slice(0, shadowStart) + softShadow + shadowChunk.slice(shadowEnd))
    if (!seabed) shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', `
      #ifdef USE_MAP
        // World-space projection keeps authored mineral detail at a physical
        // scale across long walls, crown fragments, and irregular bank stones.
        // Follow the authored eroded normals, rather than revealing each triangle
        // through a different projection on an otherwise smooth rock shoulder.
        vec3 reefNormal = normalize(vReefSurfaceNormal);
        vec3 reefWeight = pow(abs(reefNormal), vec3(4.0));
        reefWeight /= max(dot(reefWeight, vec3(1.0)), 0.0001);
        vec3 reefUv = vReefWorld * 0.67;
        vec4 mineral = texture2D(map, reefUv.yz) * reefWeight.x
          + texture2D(map, reefUv.xz) * reefWeight.y
          + texture2D(map, reefUv.xy) * reefWeight.z;
        diffuseColor *= mineral;
      #endif
    `)
    if (!seabed) shader.fragmentShader = shader.fragmentShader.replace('#include <normal_fragment_maps>', `
      #if defined(USE_NORMALMAP_TANGENTSPACE) && defined(USE_MAP)
        vec3 surfaceNormal = normalize(vReefSurfaceNormal);
        vec3 nx = texture2D(normalMap, reefUv.yz).xyz * 2.0 - 1.0;
        vec3 ny = texture2D(normalMap, reefUv.xz).xyz * 2.0 - 1.0;
        vec3 nz = texture2D(normalMap, reefUv.xy).xyz * 2.0 - 1.0;
        nx.xy *= normalScale; ny.xy *= normalScale; nz.xy *= normalScale;
        // Whiteout blend each projection with the actual geological surface.
        // A flat normal texture must preserve that surface, not pull it toward
        // one of the world axes and turn rounded shoulders into crystal facets.
        vec3 mineralNormal = vec3(abs(nx.z) * surfaceNormal.x, nx.y + surfaceNormal.y, nx.x + surfaceNormal.z) * reefWeight.x
          + vec3(ny.x + surfaceNormal.x, abs(ny.z) * surfaceNormal.y, ny.y + surfaceNormal.z) * reefWeight.y
          + vec3(nz.x + surfaceNormal.x, nz.y + surfaceNormal.y, abs(nz.z) * surfaceNormal.z) * reefWeight.z;
        normal = normalize((viewMatrix * vec4(normalize(mineralNormal), 0.0)).xyz);
      #else
        #include <normal_fragment_maps>
      #endif
    `)
    shader.fragmentShader = shader.fragmentShader.replace('#include <color_fragment>', `#include <color_fragment>
      vec2 outsideReef = max(abs(vReefWorld.xz) - reefBounds + vec2(0.5), vec2(0.0));
      float waterDepth = smoothstep(0.0, 2.8, length(outsideReef));
      diffuseColor.rgb *= mix(vec3(0.94, 0.99, 1.03), vec3(0.035, 0.25, 0.47), waterDepth);
      ${seabed ? '' : `
      float colonisation = reefNoise(vReefWorld.xz * 0.88 + vReefWorld.y * 0.22) * 0.7
        + reefNoise(vReefWorld.xz * 3.2) * 0.3;
      float algae = smoothstep(0.54, 0.76, colonisation);
      diffuseColor.rgb *= mix(vec3(1.0), vec3(0.32, 0.43, 0.30), algae * 0.64);
      `}
    `)
    shader.fragmentShader = shader.fragmentShader.replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
      vec2 current = vec2(reefTime * 0.011, reefTime * -0.008);
      float rippleA = texture2D(reefCaustics, vReefWorld.xz * 0.14 + current, 1.4).r;
      float rippleB = texture2D(reefCaustics, vReefWorld.xz * 0.105 - current * 0.7, 1.4).r;
      // Broad wave-focusing pools coexist with the smaller moving caustic filaments.
      // Both project in world space, so one pool continues across rock and sand.
      float pool = reefNoise(vReefWorld.xz * 0.38 + vec2(reefTime * 0.006, 0.0));
      float focus = mix(0.64, 1.42, smoothstep(0.25, 0.78, pool));
      reflectedLight.directDiffuse *= vec3(1.10, 1.12, 1.02) * (focus + min(rippleA, rippleB) * 2.4 + max(rippleA, rippleB) * 0.64);
      // Water's diffuse skylight is blue even where the direct sun is fully occluded.
      reflectedLight.indirectDiffuse *= vec3(0.65, 0.92, 1.10);
      reflectedLight.indirectDiffuse += diffuseColor.rgb * vec3(0.018, 0.040, 0.055);
    `)
  }
  material.customProgramCacheKey = () => `reef-light-${seabed ? 'sand' : 'stone'}-3`
}
export function preloadReefMaterials(): Promise<void> {
  const loader = new THREE.TextureLoader()
  const load = (file: string) => loader.loadAsync(`${import.meta.env.BASE_URL}textures/aaa/${file}.png`)
  applyReefKelp(materials.kelp)
  return pending ??= Promise.all([...(['limestone', 'sand'] as const).map(async name => {
    const [texture, normal] = await Promise.all([load(`${name}-albedo`), load(`${name}-normal`)])
    texture.colorSpace = THREE.SRGBColorSpace
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping
    texture.anisotropy = 8
    texture.repeat.setScalar(name === 'sand' ? 18 : 0.48)
    materials[name].map = texture
    normal.wrapS = normal.wrapT = THREE.RepeatWrapping
    normal.anisotropy = 8
    normal.repeat.copy(texture.repeat)
    materials[name].normalMap = normal
    materials[name].normalScale.setScalar(name === 'sand' ? 0.32 : 0.72)
    materials[name].needsUpdate = true
  }), load('sponge-albedo').then(texture => {
    // The authored porous surface supplies real colony detail, including the tiny
    // cup mouths on the encrusting mats, instead of unlit flat colour patches.
    texture.colorSpace = THREE.SRGBColorSpace
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping
    texture.anisotropy = 8
    texture.repeat.setScalar(1.8)
    materials.coral.map = texture
    materials.coral.bumpMap = texture
    materials.coral.bumpScale = 0.045
    materials.coral.roughness = 0.72
    materials.coral.needsUpdate = true
  }), load('caustics').then(texture => {
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping
    caustics = texture
  })]).then(() => {}).catch(error => { pending = undefined; throw error })
}
export function reefMaterials(): typeof materials { return materials }
